import { Clock } from '../../components/Clock.jsx';
import { StatCard } from '../../components/StatCard.jsx';
import { ToolTile } from '../../components/ToolTile.jsx';
import { StatsWidget } from './StatsWidget.jsx';
import { ShiftWidget } from './ShiftWidget.jsx';
import { JobsWidget } from './JobsWidget.jsx';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.jsx';

// Locked in the design brief — one ink per module, never re-recolour.
const TOOLS = [
  { href: '/inventory',         ink: '#F4C430', title: 'Inventory',         subtitle: 'Supply & reorder alerts' },
  { href: '/ink-books',         ink: '#00B4D8', title: 'Ink Books',         subtitle: 'Pantone + designer ink library' },
  { href: '/converter',         ink: '#A18AF5', title: 'Converter',         subtitle: 'Colour check & convert' },
  { href: '/maintenance',       ink: '#E63B7A', title: 'Maintenance',       subtitle: 'Issues, visits, routine logs' },
  { href: '/ink-density',       ink: '#F5EEDC', title: 'Ink Density',       subtitle: 'X-Rite recording & reports' },
  { href: '/customers',         ink: '#FF8A4C', title: 'Customers',         subtitle: 'Customer directory' },
  { href: '/customer-profiles', ink: '#FF8A4C', title: 'Customer Profiles', subtitle: 'Per-customer press setups' },
  { href: '/machines',          ink: '#5DD0C8', title: 'Machines',          subtitle: 'Printers, laminators, workstations — every machine' },
  { href: '/contacts',          ink: '#E091A8', title: 'Contacts',          subtitle: 'Suppliers, technicians, manufacturers' },
  { href: '/statistics',        ink: '#34C77B', title: 'Statistics',        subtitle: 'Trends, comparisons & KPIs' },
  { href: '/admin',             ink: '#6FB1F7', title: 'Admin',             subtitle: 'Users & settings' },
  { href: '/shifts',            ink: '#7C8FA6', title: 'Shift Tracker',     subtitle: 'Clock in/out & shift log' },
  { href: '/jobs',              ink: '#D97757', title: 'Jobs',              subtitle: 'Job tickets & to-do list' },
];

// External web portals. Favicon loads from each site's actual domain.
const SITES = [
  { href: 'https://gpihelpdesk.service-now.com/sp',          title: 'ServiceNow',      subtitle: 'IT helpdesk portal'        },
  { href: 'https://members.closetheloop.com.au/authentication/login', title: 'CloseTheLoop', subtitle: 'Member portal'      },
  { href: 'https://account.microsoft.com',                   title: 'Microsoft 365',   subtitle: 'Email, Teams, Office apps' },
  { href: 'https://github.com',                              title: 'GitHub',          subtitle: 'Code repositories'         },
  { href: 'https://site.esko.com/en/myaccount',              title: 'Esko',            subtitle: 'Account & support'         },
  { href: 'https://graphicpkg.sharepoint.com',               title: 'SharePoint',      subtitle: 'Company documents'         },
  { href: 'https://connect.pantone.com',                     title: 'Pantone Connect', subtitle: 'Colour library'            },
  { href: 'https://ess.cloud.micropay.com.au/#/signin/',     title: 'ESS',             subtitle: 'Employee self-service'     },
  { href: 'https://creativecloud.adobe.com',                 title: 'Adobe',           subtitle: 'Creative Cloud'            },
];

const PLACEHOLDER = '—';

const formatDate = () =>
  new Date().toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

export const LauncherPage = () => {
  const { user } = useAuth();
  const { data: invStats } = useApi('/inventory/stats');
  const { data: mntStats } = useApi('/maintenance/stats');
  const hour = new Date().getHours();
  const part = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';

  const STATS = [
    { label: 'Open issues',     value: mntStats?.open ?? PLACEHOLDER,      accent: '#E63B7A' },
    { label: 'Low stock',       value: invStats?.low ?? PLACEHOLDER,       accent: '#F4C430' },
    { label: 'Overdue tasks',   value: mntStats?.overdueTasks ?? PLACEHOLDER, accent: '#A18AF5' },
    { label: 'Upcoming · 7d',   value: mntStats?.upcomingThisWeek ?? PLACEHOLDER, accent: '#5DD0C8' },
  ];

  return (
    <div>
      <section class="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p class="eyebrow mb-1.5" style={{ color: '#FF8A4C' }}>
            {formatDate()}
          </p>
          <h1 class="m-0 mb-1.5 text-[34px] leading-[1.18] tracking-tight text-text-primary">
            Good {part}, {user?.displayName ?? 'there'}.
          </h1>
          <p class="m-0 text-sm text-text-secondary">
            {/* Module stats power this line later. */}
            Welcome back.
          </p>
        </div>
        <Clock />
      </section>

      <section class="mb-10 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {STATS.map((s) => (
          <StatCard key={s.label} {...s} />
        ))}
      </section>

      <section class="mb-10">
        <StatsWidget />
      </section>

      <section class="mb-10">
        <ShiftWidget />
      </section>

      <section class="mb-10">
        <JobsWidget />
      </section>

      <section class="mb-10">
        <h2 class="m-0 mb-3.5 font-serif text-xl font-medium text-text-primary">
          Tools
        </h2>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {TOOLS.map((t) => (
            <ToolTile key={t.href} {...t} />
          ))}
        </div>
      </section>

      <hr class="mb-10 border-0 border-t border-border-subtle" />

      <section>
        <h2 class="m-0 mb-3.5 font-serif text-xl font-medium text-text-primary">
          Sites
        </h2>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {SITES.map((s) => (
            <ToolTile key={s.href} external {...s} />
          ))}
        </div>
      </section>
    </div>
  );
};
